import type {
  AddReactionMutationMutation,
  AddReactionMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/addReactionMutation'
import type {
  RemoveReactionMutationMutation,
  RemoveReactionMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/removeReactionMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {Environment, RecordProxy, RecordSourceProxy} from 'relay-runtime'

type AddReactionSubject = NonNullable<NonNullable<AddReactionMutationMutation['addReaction']>['subject']>
type RemoveReactionSubject = NonNullable<NonNullable<RemoveReactionMutationMutation['removeReaction']>['subject']>

export type ReactionSubject = AddReactionSubject | RemoveReactionSubject

export type ReactionMutationVariables = AddReactionMutationMutationVariables | RemoveReactionMutationMutationVariables

export type ReactionMutationCallbacks<TSubject extends ReactionSubject> = {
  onCompleted?: (subject: TSubject) => void
  onError?: (error: Error) => void
  onStale?: () => void
}

export type ReactionMutationContext = {
  optimisticUpdate: ReturnType<typeof applyOptimisticReaction>
  mutation: symbol
}

type ReactionGroup = NonNullable<ReactionSubject['reactionGroups']>[number]
type Reactor = NonNullable<ReactionGroup['reactors']['nodes']>[number]
type ReactionContent = ReactionMutationVariables['input']['content']
const latestReactionMutations = new WeakMap<Environment, Map<string, symbol>>()

type GraphQLResponse<T> = {
  readonly data?: T
  readonly errors?: ReadonlyArray<{readonly message?: string}>
}

export async function submitReactionMutation<T>(
  method: 'DELETE' | 'POST',
  {input: {subjectId, content}}: ReactionMutationVariables,
): Promise<T> {
  const response = await reactFetchJSON('/reactable_item/reactions', {
    method,
    body: {subjectId, content},
  })

  if (!response.ok) throw new Error(`Reaction mutation failed (${response.status})`)
  const json = (await response.json()) as GraphQLResponse<T>
  if (!json.data) throw new Error(json.errors?.[0]?.message ?? 'Reaction mutation returned no data')

  return json.data
}

function findReactionGroup(store: RecordSourceProxy, subject: string, content: ReactionContent) {
  return store
    .get(subject)
    ?.getLinkedRecords('reactionGroups')
    ?.find(group => group?.getValue('content') === content)
}

export function applyOptimisticReaction(
  environment: Environment,
  {input: {subjectId, content}}: ReactionMutationVariables,
  viewerHasReacted: boolean,
) {
  const subject = String(subjectId)
  return environment.applyUpdate({
    storeUpdater: store => {
      const reactionGroup = findReactionGroup(store, subject, content)
      if (!reactionGroup) return

      reactionGroup.setValue(viewerHasReacted, 'viewerHasReacted')
      const reactors = reactionGroup.getLinkedRecord('reactors', {first: 10})
      if (!reactors) return

      const totalCount = reactors.getValue('totalCount')
      if (typeof totalCount !== 'number') return

      reactors.setValue(Math.max(0, totalCount + (viewerHasReacted ? 1 : -1)), 'totalCount')
    },
  })
}

export function startReactionMutation(environment: Environment, subject: string) {
  const mutation = Symbol()
  const mutations = latestReactionMutations.get(environment) ?? new Map<string, symbol>()
  mutations.set(subject, mutation)
  latestReactionMutations.set(environment, mutations)
  return mutation
}

export function completeReactionMutation(environment: Environment, subject: string, mutation: symbol) {
  const mutations = latestReactionMutations.get(environment)
  if (mutations?.get(subject) !== mutation) return false

  mutations.delete(subject)
  return true
}

function updateReactor(store: RecordSourceProxy, reactor: Reactor) {
  if (!reactor) return null

  const record = store.get(reactor.id) ?? store.create(reactor.id, reactor.__typename)
  record.setValue(reactor.id, 'id')
  record.setValue(reactor.login, 'login')
  record.setValue(reactor.name, 'name')
  record.setValue(reactor.avatarUrl, 'avatarUrl', {size: 64})
  if (reactor.__typename === 'Bot') {
    record.setValue(reactor.isCopilot, 'isCopilot')
    record.setValue(reactor.isAgent, 'isAgent')
    record.setValue(reactor.displayName, 'displayName')
  }

  const typeId = `client:__type:${reactor.__typename}`
  const type = store.get(typeId) ?? store.create(typeId, '__TypeSchema')
  type.setValue(true, '__isActor')
  type.setValue(true, '__isNode')

  return record
}

export function writeReactionSubjectToRelay(environment: Environment, subject: ReactionSubject) {
  environment.commitUpdate(store => {
    const subjectRecord = store.get(subject.id)
    if (!subjectRecord || !subject.reactionGroups) return

    const existingGroups = subjectRecord.getLinkedRecords('reactionGroups') ?? []
    const groupsByContent = new Map(existingGroups.map(group => [group?.getValue('content'), group]))
    const reactionGroups: RecordProxy[] = []

    for (const [index, group] of subject.reactionGroups.entries()) {
      const id = `client:${subject.id}:reactionGroups:${index}`
      const reactionGroup = groupsByContent.get(group.content) ?? store.get(id) ?? store.create(id, 'ReactionGroup')

      reactionGroup.setValue(group.content, 'content')
      reactionGroup.setValue(group.viewerHasReacted, 'viewerHasReacted')

      const reactorsId = `${id}:reactors(first:10)`
      const reactors =
        reactionGroup.getLinkedRecord('reactors', {first: 10}) ??
        store.get(reactorsId) ??
        store.create(reactorsId, 'ReactorConnection')
      reactors.setValue(group.reactors.totalCount, 'totalCount')
      reactors.setLinkedRecords(group.reactors.nodes?.map(reactor => updateReactor(store, reactor)) ?? [], 'nodes')
      reactionGroup.setLinkedRecord(reactors, 'reactors', {first: 10})
      reactionGroups.push(reactionGroup)
    }

    subjectRecord.setLinkedRecords(reactionGroups, 'reactionGroups')
  })
}
